import { providers, Signer } from 'ethers';

import { PlanNotGeneratedError } from '../utils/errors/plan-not-generated.error';
import { TokenConfigurationInterface } from './token-configuration.interface';
import { claimTopicsPlan, compliancePlan, identityRegistryPlan, PlanInterface, tokenPlan, trustedIssuersPlan } from '../factory-methods/makePlan.methods';
import { Token } from './token';

/**
 * A Factory that can be used to plan the deployment of a token, process it and follow its progress.
 * You can create a new Factory via `Token.createFactory(configuration)` or using the constructor `new TokenFactory(configuration)`.
 * Plan the deployment of the token with `await factory.makePlan()` and trigger it with `const token = await factory.deploy({ signer })`.
 */
export class TokenFactory {
  public configuration: TokenConfigurationInterface;
  private _plan!: PlanInterface[];

  constructor(configuration: TokenConfigurationInterface) {
    this.configuration = configuration;
  }

  /**
   * Retrieve the latest generated plan for the deployment of the token.
   * Note that accessing the plan without having it generated will throw an error.
   */
  get plan(): PlanInterface[] {
    if (this._plan === undefined) {
      throw new PlanNotGeneratedError();
    }
    return this._plan;
  }

  /**
   * Generate a new plan with steps required to be performed to match the desired configuration.
   * Call `factory.plan` to retrieve the generated plan.
   */
  async makePlan(): Promise<void> {
    this._plan = [
      compliancePlan(this.configuration.rules.complianceRules),
      trustedIssuersPlan(this.configuration.rules.holderRules),
      claimTopicsPlan(this.configuration.rules.holderRules),
      identityRegistryPlan(this.configuration.rules.holderRules),
      tokenPlan(this.configuration.information),
    ];
  }

  /**
   * Execute the latest generated plan and each step inside it.
   * @param signer A blockchain signer loaded with a valid provider to read and write information from an to the blockchain.
   */
  async deploy({ signer }: { signer: Signer }): Promise<Token> {
    const provider = signer.provider;
    if (!providers.Provider.isProvider(provider)) {
      throw new Error('The given signer to deploy method must also be a provider.');
    }

    if (!Signer.isSigner(signer)) {
      throw new Error('The given signer to deploy method must be a valid signer.');
    }

    if (this._plan === undefined) {
      throw new Error('A non-existing plan cannot be retrieved.');
    }

    return new Token();
  }
}
